import type { HTMLAttributes, ReactNode } from 'react';
import { Icon } from './Icon';
import './styles/page-header.css';

export type PageHeaderProps = {
  /** Page title — rendered as a semantic heading. */
  title: ReactNode;
  /** Short factual status beside the title. Prefer `status` for badges. Do not use slogans. */
  description?: string;
  /** Alias for description (Make screens used `subtitle`). */
  subtitle?: string;
  /** Secondary contextual line (e.g. business name, sync status). */
  meta?: ReactNode;
  /** Compact status aligned with the title (e.g. offline badge). */
  status?: ReactNode;
  /** Back button or other leading affordance before the title. */
  leading?: ReactNode;
  /** Primary and secondary actions. Keep one obvious primary action. */
  actions?: ReactNode;
  /** Heading level. Defaults to 1 for page titles. */
  level?: 1 | 2;
} & Omit<HTMLAttributes<HTMLElement>, 'title'>;

export function PageHeader({
  title,
  description,
  subtitle,
  meta,
  status,
  leading,
  actions,
  level = 1,
  className,
  style,
  ...rest
}: PageHeaderProps) {
  const Heading = level === 2 ? 'h2' : 'h1';
  const classes = ['cev-page-header', className].filter(Boolean).join(' ');
  const support = description ?? subtitle;

  return (
    <header className={classes} style={style} {...rest}>
      <div className="cev-page-header__main">
        {leading ? <div className="cev-page-header__leading">{leading}</div> : null}
        <div className="cev-page-header__text">
          <div className="cev-page-header__title-row">
            <Heading className="cev-page-header__title">{title}</Heading>
            {status ? <div className="cev-page-header__status">{status}</div> : null}
            {support ? <p className="cev-page-header__description">{support}</p> : null}
          </div>
          {meta ? <div className="cev-page-header__meta">{meta}</div> : null}
        </div>
      </div>
      {actions ? <div className="cev-page-header__actions">{actions}</div> : null}
    </header>
  );
}

/** Compact back affordance for PageHeader `leading`. */
export function PageHeaderBack({
  children = 'Back',
  onClick,
  className,
  ...rest
}: {
  children?: ReactNode;
  onClick: () => void;
} & Omit<HTMLAttributes<HTMLButtonElement>, 'onClick' | 'children'>) {
  const classes = ['cev-page-header-back', className].filter(Boolean).join(' ');
  return (
    <button type="button" onClick={onClick} className={classes} {...rest}>
      <Icon name="chevron-left" size={18} />
      {children}
    </button>
  );
}
